import { useRef, useState } from "react";
import axios from "axios";
import { Lock, ArrowRight, Loader2, User, Mail, Phone } from "lucide-react";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const FIELDS = [
  { k: "first_name", label: "First Name", type: "text", auto: "given-name", ph: "First Name", Icon: User },
  { k: "email", label: "Email", type: "email", auto: "email", ph: "Email", Icon: Mail },
  { k: "phone", label: "Phone", type: "tel", auto: "tel", ph: "Phone", Icon: Phone },
];

const validate = (f) => {
  const e = {};
  if (!f.first_name.trim()) e.first_name = "Please add your first name";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email";
  if (f.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number";
  return e;
};

export const LeadForm = ({ onUnlocked }) => {
  const [form, setForm] = useState({ first_name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    inFlight.current = true;
    setBusy(true);
    setServerError("");
    try {
      const email = form.email.trim().toLowerCase();
      const { data } = await axios.post(`${API}/leads`, { ...form, email });
      if (!data?.ok) throw new Error("unexpected");
      onUnlocked(email, form.first_name.trim());
    } catch (err) {
      const d = err?.response?.data?.detail;
      setServerError(typeof d === "string" ? d : "Something went wrong. Please check your details and try again.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form className="lead-form" onSubmit={submit} noValidate data-testid="lead-capture-form">
      <p className="lf-title" data-testid="form-title">Unlock the free class</p>
      {FIELDS.map((f) => (
        <label key={f.k} className="field">
          <span className="sr-only">{f.label}</span>
          <f.Icon size={17} className="field-icon" aria-hidden="true" />
          <Input
            id={f.k}
            name={f.k}
            type={f.type}
            autoComplete={f.auto}
            placeholder={f.ph}
            value={form[f.k]}
            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
            aria-invalid={!!errors[f.k]}
            className="field-input"
            data-testid={`input-${f.k.replace("_", "-")}`}
          />
          {errors[f.k] && <span className="field-error" data-testid={`error-${f.k.replace("_", "-")}`}>{errors[f.k]}</span>}
        </label>
      ))}
      <button type="submit" className="btn-gold w-full" disabled={busy} data-testid="submit-unlock-button">
        {busy ? <><Loader2 size={18} className="spin" /> Unlocking…</> : <>Watch free now <ArrowRight size={18} /></>}
      </button>
      {serverError && <p className="server-error" role="alert" data-testid="form-server-error">{serverError}</p>}
      <p className="form-micro"><Lock size={16} /> Instant access. Your information stays private.</p>
    </form>
  );
};
